import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, Loader2, ShieldAlert, FileText, Pencil, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ISSUE_CATEGORIES = [
  "Medical Care",
  "Conditions of Confinement",
  "Staff Misconduct",
  "Property",
  "Mail / Communication",
  "Legal Access",
  "Discipline",
  "Other",
];

export default function IntakeGrievance() {
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  // intakeSaved becomes { id, snapshot } once form is submitted — drives the
  // "review & generate" step before Stripe redirect.
  const [intakeSaved, setIntakeSaved] = useState(null);
  const [form, setForm] = useState({
    full_name: "", din: "", facility: "", incident_date: "",
    issue_category: "", description: "", remedy_requested: "", email: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitIntake = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: intake } = await api.post("/intake/grievance", form);
      setIntakeSaved({ id: intake.id, snapshot: { ...form } });
      toast.success("Intake saved. Review and click Generate PDF to proceed.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const generatePdf = async () => {
    if (!intakeSaved) return;
    setPaying(true);
    try {
      const { data: checkout } = await api.post("/checkout/session", {
        product_id: "grievance",
        intake_id: intakeSaved.id,
        email: intakeSaved.snapshot.email,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  if (intakeSaved) {
    const s = intakeSaved.snapshot;
    const SUMMARY_ROWS = [
      ["Name", s.full_name],
      ["DIN", s.din],
      ["Facility", s.facility],
      ["Date of Incident", s.incident_date],
      ["Issue Category", s.issue_category],
      ["Description", s.description],
      ["Remedy Requested", s.remedy_requested],
      ["Delivery Email", s.email],
    ];
    return (
      <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
        <div className="container-pl max-w-2xl">
          <p className="overline mb-4">Step 2 of 2 · Review & generate</p>
          <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
            Review your intake.
          </h1>
          <p className="text-base text-zinc-400 mb-8 max-w-xl leading-relaxed">
            Confirm everything looks right. Then click <span className="text-amber-400">Generate PDF</span> to complete a one-time $9.99 payment — your packet is delivered immediately after.
          </p>

          <div className="border border-zinc-800 rounded-sm bg-zinc-950/60 mb-6" data-testid="grievance-review-card">
            <div className="px-5 py-3 border-b border-zinc-800 bg-blue-950/20 flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-400" strokeWidth={1.5}/>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-400">Auto Grievance · DOCCS Directive #4040</p>
            </div>
            <dl className="divide-y divide-zinc-800">
              {SUMMARY_ROWS.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 px-5 py-3">
                  <dt className="text-xs uppercase tracking-wider text-zinc-500 col-span-1">{k}</dt>
                  <dd className="text-sm text-zinc-200 col-span-2 whitespace-pre-wrap break-words">{v || <span className="text-zinc-600 italic">—</span>}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-4 mb-6 flex gap-3">
            <Sparkles className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-xs text-zinc-300 leading-relaxed">
              The grievance is generated automatically from these answers in DOCCS Directive #4040 format. After payment, the PDF is available for instant download and printing.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-zinc-800 pt-6">
            <Button type="button" variant="outline" onClick={() => setIntakeSaved(null)} className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-900" data-testid="grievance-edit-btn">
              <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5}/> Edit answers
            </Button>
            <Button type="button" size="lg" onClick={generatePdf} disabled={paying} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white px-6" data-testid="grievance-generate-btn">
              {paying ? <><Loader2 className="h-4 w-4 animate-spin mr-2"/> Opening checkout…</> : <>Generate PDF — $9.99 <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5}/></>}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 italic mt-4 text-center">
            Secure payment via Stripe. We are not attorneys and do not provide legal advice.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
      <div className="container-pl max-w-2xl">
        <p className="overline mb-4">Auto Grievance · $9.99</p>
        <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
          Grievance Intake
        </h1>
        <p className="text-base text-zinc-400 mb-8 max-w-xl leading-relaxed">
          Answer 6 quick questions. We generate a formatted DOCCS Directive #4040 grievance ready to file.
        </p>

        <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-4 mb-8 flex gap-3" data-testid="grievance-notice">
          <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-zinc-300 leading-relaxed">
            This is a document preparation service. We are not attorneys and do not provide legal advice. The completed grievance is informational and administrative support only.
          </p>
        </div>

        <form onSubmit={submitIntake} className="space-y-5" data-testid="grievance-intake-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">Full Name</Label>
              <Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-name"/>
            </div>
            <div>
              <Label className="text-zinc-300">DIN</Label>
              <Input required value={form.din} onChange={(e) => update("din", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-din"/>
            </div>
            <div>
              <Label className="text-zinc-300">Facility</Label>
              <Input required value={form.facility} onChange={(e) => update("facility", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-facility"/>
            </div>
            <div>
              <Label className="text-zinc-300">Date of Incident</Label>
              <Input type="date" required value={form.incident_date} onChange={(e) => update("incident_date", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-date"/>
            </div>
          </div>

          <div>
            <Label className="text-zinc-300">Issue Category</Label>
            <Select value={form.issue_category} onValueChange={(v) => update("issue_category", v)} required>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100" data-testid="g-category"><SelectValue placeholder="Choose a category"/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {ISSUE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-300">Description of Incident</Label>
            <Textarea required rows={6} value={form.description} onChange={(e) => update("description", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-description" placeholder="What happened? When? Who was involved? Be factual and specific."/>
          </div>

          <div>
            <Label className="text-zinc-300">Remedy / Relief Requested</Label>
            <Textarea required rows={3} value={form.remedy_requested} onChange={(e) => update("remedy_requested", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-remedy" placeholder="What outcome are you asking for?"/>
          </div>

          <div>
            <Label className="text-zinc-300">Delivery Email (for the finished PDF)</Label>
            <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="g-email" placeholder="you@example.com"/>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
            <div>
              <p className="text-sm text-zinc-400">Total</p>
              <p className="display-serif text-2xl text-amber-400">$9.99 <span className="text-xs text-zinc-500 font-mono">USD</span></p>
            </div>
            <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white px-6" data-testid="g-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Review intake <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></>}
            </Button>
          </div>
          <Link to="/services" className="block text-sm text-zinc-400 hover:text-amber-400 transition-colors pt-2">← Back to services</Link>
        </form>
      </div>
    </div>
  );
}
